/**
 * RegistrationResolutionDialog
 *
 * Dialog wrapper for :class:`RegistrationResolutionWorkspace`, used as
 * the row-click surface on the Exception Requests list. Takes a
 * holding UUID (not an issue UUID) so the workspace can render every
 * open :class:`WorkflowIssue` on that holding as one cohesive task,
 * rather than opening one issue at a time.
 *
 * Mirrors the team-approved HTML preview at
 * ``~/.claude/plans/resolution-workspace-preview.html`` - numbered
 * stepper, multi-select pills, always-visible Claim History,
 * expandable Registration Details, confetti on activate.
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { useHolding } from '@/admin/hooks/use-products'
import { useClaims } from '@/admin/hooks/use-claims'
import { useFormatters } from '@/shared/lib/formatting'

import { RegistrationResolutionWorkspace } from './registration-resolution-workspace'

interface RegistrationResolutionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** UUID of the holding whose open issues drive the workspace. */
  holdingUuid: string | null
}

export function RegistrationResolutionDialog({
  open,
  onOpenChange,
  holdingUuid,
}: RegistrationResolutionDialogProps) {
  const { can } = usePermissions()
  const { locale } = useFormatters()
  const { data: holding } = useHolding(holdingUuid)
  const { data: claimsData } = useClaims(
    holdingUuid ? { holding_uuid: holdingUuid } : undefined,
  )

  const claims = (claimsData?.results ?? []).map((c) => {
    const status = (c.status ?? '').toLowerCase()
    const statusVariant: 'open' | 'paid' | 'closed' =
      status.includes('open') || status.includes('review') || status.includes('pending')
        ? 'open'
        : status.includes('paid') || status.includes('settled') || status.includes('approved')
          ? 'paid'
          : 'closed'
    const raised =
      (c as { raised_at?: string | null; created_at?: string | null }).raised_at ??
      (c as { created_at?: string | null }).created_at ??
      ''
    return {
      uuid: c.uuid,
      raisedOn: raised
        ? new Date(raised).toLocaleDateString(locale, {
            day: '2-digit',
            month: 'short',
            year: '2-digit',
          })
        : '-',
      summary:
        (c as { summary?: string; cause?: string }).summary ??
        (c as { cause?: string }).cause ??
        'Claim',
      statusLabel:
        statusVariant === 'open'
          ? 'Open'
          : statusVariant === 'paid'
            ? 'Paid'
            : 'Closed',
      statusVariant,
    }
  })

  const productLabel =
    holding?.product?.cached_short_name ||
    holding?.product?.cached_long_name ||
    'registration'

  // Subject line for the dialog header - echoes the HTML preview's
  // page title so the dialog feels like a focused page, not a modal.
  const title = holding?.product?.cached_short_name ?? 'Registration'
  const subtitleBits: string[] = []
  if (holdingUuid) subtitleBits.push(holdingUuid.slice(0, 8))
  const consumer = holding?.consumer_account
  const consumerName = consumer
    ? [consumer.first_name, consumer.last_name].filter(Boolean).join(' ').trim()
    : ''
  if (consumerName) subtitleBits.push(consumerName)
  if (holding?.partnership?.downstream_organisation_name) {
    subtitleBits.push(holding.partnership.downstream_organisation_name)
  } else if (holding?.organisation?.name) {
    subtitleBits.push(holding.organisation.name)
  }

  // a11y-only - the dialog must announce a title even though we render
  // our own editorial header inside the workspace.
  const srTitle = `${title} - draft resolution`
  const srSubtitle = subtitleBits.join(' · ')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        bare
        className="max-w-[1180px] sm:max-w-[1180px] max-h-[min(94vh,980px)] overflow-hidden p-0"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{srTitle}</DialogTitle>
          {srSubtitle ? <DialogDescription>{srSubtitle}</DialogDescription> : null}
        </DialogHeader>
        <div className="flex max-h-[min(94vh,980px)] min-h-0 flex-1 flex-col overflow-y-auto bg-[var(--bg)]">
          {holdingUuid ? (
            <RegistrationResolutionWorkspace
              holdingUuid={holdingUuid}
              canApprove={
                can('exceptions.approve') || can('registrations.exceptions.approve')
              }
              warrantyIntentLabel={productLabel}
              claims={claims}
              onClose={() => onOpenChange(false)}
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  )
}
